import { FC, useMemo } from 'react';
import { Card, Col, Progress, Row, Typography } from 'antd';
import { differenceInCalendarDays } from 'date-fns';
import classNames from 'classnames';
import { Trans } from 'react-i18next';
import i18n from 'i18next';

import { useAppSelector, useTranslate } from 'hooks';

import { Object, OverviewBlockProps } from 'models';

import Utils from 'utils/utils';
import { LoyaltyIcons } from 'utils/constants';

import { Skeleton } from 'componets/ui';

import { ReactComponent as BasicIcon } from 'assets/icons/basic-icon.svg';
import { ReactComponent as PlatinumIcon } from 'assets/icons/platinum-circle.svg';

import { selectUser } from 'selectors';

import styles from './index.module.scss';

const DAYS = 90;

const TEXTS: Object = {
	Silver: i18n.t('loyaltySilverText'),
	Gold: i18n.t('loyaltyGoldText'),
	Platinum: i18n.t('loyaltyPlatinumText'),
};

export const LoyaltyStatus: FC<OverviewBlockProps> = ({ isLoading }) => {
	const { loyalty, data: overview } = useAppSelector((state) => state.overview);
	const user = useAppSelector(selectUser);

	const {
		loyaltyStatusText,
		unavailableLoyaltyStatusText,
		daysText,
		dayText,
		loyaltyStatusBasicText,
		platinumCategoryText,
		yourStatusText,
		yourPersonalBonusText,
	} = useTranslate([
		'loyaltyStatusText',
		'unavailableLoyaltyStatusText',
		'daysText',
		'dayText',
		'loyaltyStatusBasicText',
		'platinumCategoryText',
		'yourStatusText',
		'yourPersonalBonusText',
	]);

	const balance = +overview.totalBalance;

	const currentPlan = user.category;

	const nextPlan = useMemo(
		() => loyalty?.find((plan) => plan.locked) || loyalty[loyalty.length - 1],
		[loyalty],
	);

	const plan = loyalty?.find((item) => item.title === currentPlan?.title);

	const Icon = plan ? LoyaltyIcons[plan?.title] : currentPlan?.slug === 'default' ? BasicIcon : '';
	const text = plan
		? TEXTS[plan?.title]
		: currentPlan?.slug === 'default'
		? loyaltyStatusBasicText
		: '';

	const isPlatinum = plan?.title === 'Platinum';

	const isBasic = !currentPlan?.isLoyalty && !currentPlan?.isDefault;

	const percent = isPlatinum
		? (+balance / plan?.minAmount) * 100
		: (+balance / nextPlan?.minAmount) * 100;

	const days = differenceInCalendarDays(new Date(), new Date(user.registrationDate));
	const isUnavailableLoyalty = days < DAYS;

	return (
		<Card
			bordered={false}
			className={classNames(styles.root, {
				[styles.black]: isPlatinum && !isUnavailableLoyalty,
			})}
		>
			<Skeleton loading={isLoading}>
				{isPlatinum && !isUnavailableLoyalty ? (
					<Col className={styles.col}>
						<PlatinumIcon />
						<Col className={styles.platinum}>
							<Typography.Title type="secondary" level={4}>
								{platinumCategoryText}
							</Typography.Title>
							<Typography.Text className={styles.text} type="secondary">
								{text}
							</Typography.Text>
						</Col>
					</Col>
				) : isBasic ? (
					<Row gutter={[0, 0]} style={{ marginBottom: 18 }}>
						<Col style={{ width: '100%' }}>
							<Typography.Title level={4}>{loyaltyStatusText}</Typography.Title>

							<Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
								<Typography.Text type="secondary">{yourStatusText}:</Typography.Text>

								<BasicIcon />
							</Row>

							<Row justify="space-between" align="middle">
								<Typography.Text type="secondary">{yourPersonalBonusText}:</Typography.Text>
								<Typography.Text>{currentPlan?.interestRate}%</Typography.Text>
							</Row>
						</Col>
					</Row>
				) : (
					<>
						<Row gutter={[0, 0]} style={{ marginBottom: 18 }}>
							<Col style={{ width: '100%' }}>
								<Typography.Title level={4}>{loyaltyStatusText}</Typography.Title>

								{isUnavailableLoyalty
									? ''
									: Icon && (
											<Row justify="space-between" align="middle" style={{ marginBottom: 32 }}>
												<Typography.Text type="secondary">{yourStatusText}:</Typography.Text>

												<Icon />
											</Row>
									  )}

								<Typography.Text type="secondary">
									{isUnavailableLoyalty ? unavailableLoyaltyStatusText : text}
								</Typography.Text>
							</Col>
						</Row>
						<Row gutter={[0, 12]} justify="space-between">
							<Col span={24}>
								<Progress
									strokeColor={{
										'0%': '#65ABFF',
										'100%': '#FB827A',
									}}
									percent={isUnavailableLoyalty ? 0 : percent}
									showInfo={false}
								/>
							</Col>

							{isUnavailableLoyalty ? (
								<Col>
									<Typography.Text type="secondary">
										<Trans
											i18nKey="daysLeftKickOffText"
											values={{
												days: DAYS - days,
												textDays: DAYS - days === 1 ? dayText : daysText,
											}}
										/>
									</Typography.Text>
								</Col>
							) : (
								<Col className={styles.values}>
									<Col className={styles.row}>
										<Typography.Text type="secondary">
											{Utils.moneyFormat(balance)}/
										</Typography.Text>
										<Typography.Text type="secondary">
											{Utils.moneyFormat(Number(nextPlan?.minAmount))}
										</Typography.Text>
									</Col>
									<Col className={styles.row}>
										<Typography.Text type="secondary">{nextPlan?.title}&nbsp;</Typography.Text>
										<Typography.Text type="secondary">(+{nextPlan?.percent}%)</Typography.Text>
									</Col>
								</Col>
							)}
						</Row>
					</>
				)}
			</Skeleton>
		</Card>
	);
};
